import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import { toast } from "react-toastify";
import { RecordingPlayer } from "../recording-player/recording-player";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { CustomMarkdown } from "../../custom-markdown/custom-markdown";
import {
  ArrowForwardIcon,
  DownloadIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "../../../assets/icon";
import {
  getRealtimeReport,
  postRealtimeMenu,
} from "../../../axios/realtimeSpeech";
import {
  outerContainerStyles,
  recordingContainerStyles,
  recordingHeaderStyles,
  replayModeSelectStyles,
  shortTranscriptContainerStyles,
  moreButtonStyles,
  highlightsButtonStyles,
  menuContainerStyles,
  downloadButtonStyles,
  reportStyles,
  coachingModelButtonStyles,
} from "./styles";
import { RealtimeDialog } from "@/types/realtimeSpeech";
import { useAppStore } from "../../../stores/appStore";
import { CoachingModelDialog } from "../coaching-model-dialog/coaching-model-dialog";
import { useDeviceType } from "../../../hooks/useDeviceType";
import { playActionSound } from "../../../helpers/actionSound";
import { MobileWarningModal } from "../mobile-warning-modal/mobile-warning-modal";

interface RecordingBlockProps {
  recording: RealtimeDialog;
  isPlaying: boolean;
  setIsPlaying: (value: boolean) => void;
  playbackError?: string;
  onAudioError: () => void;
  onClearPlaybackError?: () => void;
  onOpenTranscript: () => void;
  isHighlightsOpen?: boolean;
  onCloseHighlights?: () => void;
}

type DisplayMode = "full" | "short" | "none";

const safeString = (value: unknown, fallback = "Not provided"): string =>
  typeof value === "string" && value.trim() ? value : fallback;

const formatDate = (timestamp?: string | null): [string, string] => {
  if (!timestamp || typeof timestamp !== "string") {
    return ["Not provided", ""];
  }

  const [datePart, timePart] = timestamp.split("T");
  if (!datePart) {
    return ["Not provided", ""];
  }

  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];

  const time = timePart ? timePart.split(".")[0] : "";

  if (datePart === todayStr) {
    return ["Today", time];
  }

  return [datePart, time];
};

const MAX_TRANSCRIPT_LENGTH = 150;
const MAX_REPORT_LENGTH = 600;

export const RecordingBlock: React.FC<RecordingBlockProps> = ({
  recording,
  isPlaying,
  setIsPlaying,
  playbackError,
  onAudioError,
  onClearPlaybackError,
  onOpenTranscript,
  isHighlightsOpen = false,
  onCloseHighlights,
}: RecordingBlockProps) => {
  const theme = useTheme();
  const { setSellingDialogue, setLiveSpeechMode, setRealtimeMenuOptions } =
    useAppStore();
  const deviceType = useDeviceType();

  const safeRoleplayTitle = safeString(recording.roleplay_title);

  let fullTranscript = safeRoleplayTitle;

  const dialogueTranscriptionOnly =
    typeof recording.dialogue_transcription_only === "string"
      ? recording.dialogue_transcription_only.trim()
      : "";

  if (dialogueTranscriptionOnly) {
    fullTranscript = dialogueTranscriptionOnly;
  } else if (
    recording.transcript &&
    typeof recording.transcript === "object" &&
    !Array.isArray(recording.transcript)
  ) {
    const entries = Object.entries(recording.transcript);
    if (entries.length > 0) {
      fullTranscript = entries
        .map(([key, value]) => {
          if (!value || typeof value !== "object") {
            return `${key} s - Not provided`;
          }
          const text =
            typeof (value as any).text === "string" &&
            (value as any).text.trim()
              ? (value as any).text
              : "Not provided";
          return `${key} s - ${text}`;
        })
        .join("\n");
    }
  }

  if (!fullTranscript || !fullTranscript.trim()) {
    fullTranscript = "Not provided";
  }

  const rawReport = safeString(recording.final_coaching_report);
  const [reportDisplayMode, setReportDisplayMode] =
    useState<DisplayMode>("short");

  const [audioReplayMode, setAudioReplayMode] =
    useState<string>("Default Replay");
  const [transcriptDisplayMode, setTranscriptDisplayMode] =
    useState<DisplayMode>("short");
  const [isCoachingModelDialogOpen, setIsCoachingModelDialogOpen] =
    useState<boolean>(false);
  const [submittingCoachingMode, setSubmittingCoachingMode] = useState<
    "video" | "audio" | null
  >(null);
  const [isMobileWarningOpen, setIsMobileWarningOpen] =
    useState<boolean>(false);

  let transcript = fullTranscript;
  if (
    transcriptDisplayMode === "short" &&
    transcript !== "Not provided" &&
    transcript.length > MAX_TRANSCRIPT_LENGTH
  ) {
    transcript = transcript.slice(0, MAX_TRANSCRIPT_LENGTH - 3) + "...";
  }

  let report = rawReport;
  if (
    reportDisplayMode === "short" &&
    report !== "Not provided" &&
    report.length > MAX_REPORT_LENGTH
  ) {
    report = report.slice(0, MAX_REPORT_LENGTH - 3) + "...";
  }

  const [date, time] = formatDate(
    typeof recording.roleplay_timestamp === "string"
      ? recording.roleplay_timestamp
      : null
  );

  const handleModeChange = (newMode: string) => {
    setIsPlaying(false);
    setAudioReplayMode(newMode);
    onClearPlaybackError?.();
  };

  const handleFileDownload = async () => {
    try {
      const res = await getRealtimeReport(recording.id);

      if (!res || !res.pdf) {
        throw new Error("Empty PDF data");
      }

      const byteCharacters = atob(res.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const url = URL.createObjectURL(blob);

      const newWindow = window.open(url, "_blank");

      if (!newWindow) {
        const link = document.createElement("a");
        link.href = url;
        link.download = res.filename || "report.pdf";
        link.click();
        toast.success(
          `PDF "${res.filename || "report.pdf"}" downloaded successfully!`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
      } else {
        toast.success(
          `PDF "${res.filename || "report.pdf"}" opened in new tab!`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
      }

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 5000);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to generate PDF report. Please try again.", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleCoachingModel = async () => {
    setIsPlaying(false);

    if (deviceType !== "desktop") {
      await playActionSound();
      setIsMobileWarningOpen(true);
      return;
    }

    setIsCoachingModelDialogOpen(true);
  };

  const handleMobileWarningContinue = () => {
    setIsMobileWarningOpen(false);
    setIsCoachingModelDialogOpen(true);
  };

  const handleMobileWarningExit = () => {
    setIsMobileWarningOpen(false);
  };

  const handleCoachingModelSelect = async (audioOnly: boolean) => {
    setSubmittingCoachingMode(audioOnly ? "audio" : "video");
    setIsCoachingModelDialogOpen(false);

    try {
      const response = await postRealtimeMenu({
        record_id: recording.id,
        realtime_mode: "coach_me",
        audio_only: audioOnly,
      });

      if (response?.error) {
        toast.error(response.error, {
          position: "top-right",
          autoClose: 5000,
        });
        setSubmittingCoachingMode(null);
        return;
      }

      setRealtimeMenuOptions({
        record_id: recording.id,
        realtime_mode: "coach_me",
        audio_only: audioOnly,
      });
      setLiveSpeechMode("coach_me");
      setSellingDialogue(true);
      setSubmittingCoachingMode(null);
    } catch (error) {
      console.error("Error starting coaching:", error);
      toast.error("Failed to start coaching. Please try again.", {
        position: "top-right",
        autoClose: 4000,
      });
      setSubmittingCoachingMode(null);
    }
  };

  const handleCloseCoachingModelDialog = () => {
    if (!submittingCoachingMode) {
      setIsCoachingModelDialogOpen(false);
    }
  };

  const handleToggleTranscriptMode = () => {
    setTranscriptDisplayMode((prev) => {
      switch (prev) {
        case "full":
          return "short";
        case "short":
          return "full";
        default:
          return "short";
      }
    });
  };

  const handleToggleReportMode = () => {
    setReportDisplayMode((prev) => {
      switch (prev) {
        case "full":
          return "short";
        case "short":
          return "full";
        default:
          return "none";
      }
    });
  };

  const score =
    typeof recording.score === "number" || typeof recording.score === "string"
      ? recording.score
      : "Not provided";

  const hasBestPracticeAudio =
    typeof recording.best_practice_dialogue_path === "string" &&
    recording.best_practice_dialogue_path.trim().length > 0;

  const hasCoachingAudio =
    typeof recording.coaching_path === "string" &&
    recording.coaching_path.trim().length > 0;

  const hasDefaultAudio =
    typeof recording.audio_url === "string" &&
    recording.audio_url.trim().length > 0;

  const recordingTitleShort =
    safeRoleplayTitle.length > 15
      ? `${safeRoleplayTitle.slice(0, 15)}...`
      : safeRoleplayTitle;

  const hasTranscriptObject =
    recording.transcript &&
    typeof recording.transcript === "object" &&
    !Array.isArray(recording.transcript) &&
    Object.keys(recording.transcript).length > 0;

  const renderPlayer = () => {
    if (playbackError) {
      return (
        <Box
          sx={{
            px: 2,
            py: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "error.light",
            backgroundColor: (t) =>
              t.palette.mode === "dark"
                ? "rgba(211, 47, 47, 0.12)"
                : "rgba(211, 47, 47, 0.06)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: 0.5,
            minWidth: { xs: "100%", md: 260 },
          }}
        >
          <CustomTypography
            fontSize={14}
            fontWeight={600}
            color={theme.palette.error.main}
          >
            Playback unavailable
          </CustomTypography>
          <CustomTypography
            fontSize={13}
            fontWeight={400}
            color={theme.palette.modalColors.colorLable}
          >
            {playbackError}
          </CustomTypography>
        </Box>
      );
    }

    if (hasBestPracticeAudio && audioReplayMode === "Best Practice Replay") {
      return (
        <RecordingPlayer
          audioUrl={recording.best_practice_dialogue_path as string}
          title="Best Practice Replay"
          timestamp={`${date} ${time}`}
          isPlaying={isPlaying}
          onPlay={() => setIsPlaying(true)}
          onLoadingError={onAudioError}
        />
      );
    }

    if (hasCoachingAudio && audioReplayMode === "Coaching") {
      return (
        <RecordingPlayer
          audioUrl={recording.coaching_path as string}
          title="Coaching"
          timestamp={`${date} ${time}`}
          isPlaying={isPlaying}
          onPlay={() => setIsPlaying(true)}
          onLoadingError={onAudioError}
        />
      );
    }

    if (hasDefaultAudio) {
      return (
        <RecordingPlayer
          audioUrl={recording.audio_url as string}
          title={recordingTitleShort}
          timestamp={`${date} ${time}`}
          isPlaying={isPlaying}
          onPlay={() => setIsPlaying(true)}
          onLoadingError={onAudioError}
        />
      );
    }

    return (
      <Box
        sx={{
          px: 2,
          py: 3,
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CustomTypography
          fontSize={14}
          fontWeight={400}
          color={theme.palette.modalColors.colorLable}
        >
          Audio: Not provided
        </CustomTypography>
      </Box>
    );
  };

  const handleRecordingBlockClick = (e: React.MouseEvent) => {
    if (isHighlightsOpen && onCloseHighlights) {
      onCloseHighlights();
    }
  };

  return (
    <Box
      sx={outerContainerStyles}
      data-recording-block
      onClick={(e) => {
        // Don't propagate to parent if clicking on interactive elements
        const target = e.target as HTMLElement;
        if (
          target.closest("button") ||
          target.closest("select") ||
          target.closest(".MuiFormControl-root")
        ) {
          e.stopPropagation();
          return;
        }
        // Handle click on recording block
        handleRecordingBlockClick(e);
      }}
    >
      <Box
        key={
          typeof recording.roleplay_timestamp === "string"
            ? recording.roleplay_timestamp
            : recording.id
        }
        sx={recordingContainerStyles}
        style={{ cursor: isHighlightsOpen ? "pointer" : "default" }}
      >
        <Box onClick={(e) => e.stopPropagation()}>{renderPlayer()}</Box>

        <Box sx={shortTranscriptContainerStyles}>
          <Box sx={recordingHeaderStyles}>
            <CustomTypography
              fontSize={14}
              fontWeight={400}
              color={theme.palette.modalColors.colorLable}
            >
              <span key="score" className="font-bold">
                Score:
              </span>
              <span key="score-value"> {score}</span>
            </CustomTypography>

            {(hasBestPracticeAudio || hasCoachingAudio || hasDefaultAudio) && (
              <FormControl
                size="small"
                sx={{ minWidth: 60 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Select
                  value={audioReplayMode}
                  onChange={(e) => handleModeChange(e.target.value)}
                  sx={replayModeSelectStyles}
                >
                  <MenuItem value={"Default Replay"}>Default Replay</MenuItem>
                  {hasCoachingAudio && (
                    <MenuItem value={"Coaching"}>Coaching</MenuItem>
                  )}
                  {hasBestPracticeAudio && (
                    <MenuItem value={"Best Practice Replay"}>
                      Best Practice Replay
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </Box>

          <CustomTypography
            fontSize={14}
            fontWeight={400}
            color={theme.palette.modalColors.colorLable}
          >
            <CustomMarkdown>{transcript}</CustomMarkdown>

            {fullTranscript !== "Not provided" &&
              fullTranscript.length > MAX_TRANSCRIPT_LENGTH && (
                <Button
                  key="more-btn"
                  variant="contained"
                  sx={moreButtonStyles}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleTranscriptMode();
                  }}
                >
                  <span>
                    {transcriptDisplayMode === "full" ? "Less" : "More"}
                  </span>{" "}
                  {transcriptDisplayMode === "full" ? (
                    <ArrowUpIcon />
                  ) : (
                    <ArrowDownIcon />
                  )}
                </Button>
              )}
          </CustomTypography>

          {hasTranscriptObject && (
            <Box sx={menuContainerStyles} onClick={(e) => e.stopPropagation()}>
              <Button
                variant="contained"
                sx={highlightsButtonStyles}
                onClick={() => {
                  if (isHighlightsOpen && onCloseHighlights) {
                    onCloseHighlights();
                  } else {
                    onOpenTranscript();
                  }
                }}
              >
                <span>Highlights</span>
                <ArrowForwardIcon style={{ fontSize: "14px" }} />
              </Button>

              <Button
                variant="contained"
                sx={coachingModelButtonStyles}
                onClick={handleCoachingModel}
              >
                <span>Coaching</span>
                <ArrowForwardIcon style={{ fontSize: "14px" }} />
              </Button>

              <Button
                variant="contained"
                sx={downloadButtonStyles}
                onClick={handleFileDownload}
              >
                <DownloadIcon style={{ strokeWidth: 3, fontSize: "14px" }} />
                <span>Export to PDF</span>
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <CustomTypography sx={reportStyles}>
        <span className="font-bold">Final Coaching Report:</span>
        <br />
        <CustomMarkdown>{report}</CustomMarkdown>
        {rawReport !== "Not provided" &&
          rawReport.length > MAX_REPORT_LENGTH && (
            <Button
              variant="outlined"
              sx={moreButtonStyles}
              onClick={handleToggleReportMode}
            >
              <span>{reportDisplayMode === "full" ? "Less" : "More"}</span>{" "}
              {reportDisplayMode === "full" ? (
                <ArrowUpIcon />
              ) : (
                <ArrowDownIcon />
              )}
            </Button>
          )}
      </CustomTypography>

      <CoachingModelDialog
        open={isCoachingModelDialogOpen}
        onClose={handleCloseCoachingModelDialog}
        onSelect={handleCoachingModelSelect}
        submittingMode={submittingCoachingMode}
      />
      <MobileWarningModal
        open={isMobileWarningOpen}
        onContinue={handleMobileWarningContinue}
        onExit={handleMobileWarningExit}
      />
    </Box>
  );
};
